/**
 * options = what may this school pick (grid). pricing = what does this basket cost.
 * Every displayed price comes from pricing. Options is never anchored (packagesPromos stays empty);
 * the renewal panels do pass subjectsIDs, because each auto-renew group asks for its own subjects.
 */

export enum OptionsType {
  ADD = 'ADD',
  UPGRADE = 'UPGRADE',
  UPGRADE_FREE_TRIAL = 'UPGRADE_FREE_TRIAL',
  RENEW = 'RENEW',
}

export type PackagePromo = {
  packagesId: number;
  promoCode: string;
};

export type OptionsRequest = {
  packagesPromos: PackagePromo[];
  subjectsIDs: number[];
  optionsType: OptionsType;
};

export type PricingOrderRequest = {
  orderNumber: number;
  packagePromos: PackagePromo[];
};

export type PricingRequest = {
  orders: PricingOrderRequest[];
};

export type PackageResult = {
  packagesID: number;
  generalPackagesID: number;
  subscriptionTypesID: number;
  subjectsID: number;
  generalPackagesName: string;
  subscriptionTypesName: string;
  subscriptionTypesDuration: number;
  subjectsName: string;
  subjectsShortName: string;
  netUndiscounted: number;
  netDiscounted: number;
  netSaving: number;
  grossUndiscounted: number;
  grossDiscounted: number;
  promoCode?: string | null;
  promoCodeDiscountApplied?: boolean;
  multiPurchaseDiscountApplied?: boolean;
  unbrokenServiceDiscountApplied?: boolean;
  [key: string]: any;
};

export type OrderDiscountResult = {
  packageResults: PackageResult[];
  totalNetUndiscounted: number;
  totalNetDiscounted: number;
  totalNetSaving: number;
  totalGrossUndiscounted: number;
  totalGrossDiscounted: number;
  totalGrossSaving: number;
  totalVatOnDiscounted: number;
  vatRate: number;
  bChargeVAT?: boolean;
  [key: string]: any;
};

export type OptionsOrderResult = {
  orderNumber: number;
  orderDiscountResult: OrderDiscountResult;
};

export type PricingOrderResult = OptionsOrderResult & {
  bGeneralPackagesIdMatch: boolean;
  bSubscriptionTypesIdMatch: boolean;
};

export type PricingResponse = {
  orderResults: PricingOrderResult[];
  comparison?: any;
};

export type OptionsCell = {
  generalPackagesId: number;
  subscriptionTypesId: number;
  orderNumbers: number[];
};

export type SubjectOptions = {
  subjectsId: number;
  bMultipleGeneralPackages: boolean;
  bMultipleSubscriptionTypes: boolean;
  cells: OptionsCell[];
};

export type SubjectInfo = {
  subjectsId: number;
  subjectsName: string;
  subjectsShortName: string;
};

export type GeneralPackageInfo = {
  generalPackagesId: number;
  generalPackagesName: string;
};

export type SubscriptionTypeInfo = {
  subscriptionTypesId: number;
  subscriptionTypesName: string;
  subscriptionTypesDuration: number;
};

export type SchoolUserToUpgrade = {
  schoolUsersId: number;
  schoolUsersFullName: string;
};

export type AccountContext = {
  vatRate: number;
  bChargeVAT: boolean;
  bValidVATNumber: boolean;
  locationName: string | null;
  financeEmail: string | null;
  bAddSecondSchoolUser: boolean;
  bChooseAddOrUpgrade: boolean;
  existingSubStartDate: string | null;
  bAnyFreeTrialSubscription: boolean;
  bAnyFullPaidSubscription: boolean;
  schoolUsersToUpgrade: SchoolUserToUpgrade[];
};

export type OptionsResponse = {
  orderResults: OptionsOrderResult[];
  subjectOptions: SubjectOptions[];
  bMultipleSubjects: boolean;
  subjects: SubjectInfo[];
  generalPackages: GeneralPackageInfo[];
  subscriptionTypes: SubscriptionTypeInfo[];
  accountContext: AccountContext | null;
};

/* ---- Frontend grid model (derived from OptionsResponse) ---- */

export type GridCell = {
  generalPackagesId: number;
  subscriptionTypesId: number;
  packagesId: number;
  orderNumber: number;
  package: PackageResult;
};

export type GridSubject = SubjectInfo & {
  bMultipleGeneralPackages: boolean;
  bMultipleSubscriptionTypes: boolean;
  tiers: GeneralPackageInfo[];
  lengths: SubscriptionTypeInfo[];
  cells: GridCell[];
};

export type OptionsGridModel = {
  bMultipleSubjects: boolean;
  subjects: GridSubject[];
  /** Table column headers: every length offered across all subjects. */
  lengths: SubscriptionTypeInfo[];
};

/** subjectsId -> packagesId */
export type GridSelection = { [subjectsId: number]: number };

/** subjectsId -> the promo code sent to pricing for that subject. */
export type PromoCodeBySubject = { [subjectsId: number]: string };

/** What gets priced and paid together. Keyed by subject so each package carries its own code. */
export type Basket = {
  selection: GridSelection;
  /** From getrenewalpromocode. Subjects without a code are absent. */
  promoCodeBySubject: PromoCodeBySubject;
  /** Subjects shown and priced but left out of the payment and its totals. */
  pendingSubjectIds?: number[];
};
